import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim();
console.log("REACT_APP_BACKEND_URL", BACKEND_URL);
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
console.log("BACKEND_URL", BACKEND_URL, "API_BASE", API_BASE);
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true, // send/receive httpOnly auth cookie
});

// Automatically attach JWT to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// 401 / session-revoked handling.
// Subscribers are registered via `onAuthError(fn)` — AuthContext registers a
// single handler that shows a toast and redirects to /login. We avoid a hard
// `window.location` redirect here so we can keep the React router in sync.
// ---------------------------------------------------------------------------
const errorSubscribers = new Set();
export function onAuthError(fn) {
  errorSubscribers.add(fn);
  return () => errorSubscribers.delete(fn);
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const code = (detail && typeof detail === "object") ? detail.code : null;
    if (status === 401 || status === 403) {
      errorSubscribers.forEach((fn) => {
        try { fn({ status, code, detail }); } catch (_) { /* ignore */ }
      });
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// REST helpers
// ---------------------------------------------------------------------------
export const fetchInstruments = () => api.get("/instruments").then((r) => r.data);
export const fetchInstrument  = (symbol, params = {}) =>
  api.get(`/instruments/${symbol}`, { params }).then((r) => r.data);
export const fetchEngines     = () => api.get("/engines").then((r) => r.data);
export const fetchSession     = () => api.get("/session").then((r) => r.data);

// Phase 3B
export const fetchSetups = (params = {}) =>
  api.get("/setups", { params }).then((r) => r.data);
export const fetchSetup = (id) =>
  api.get(`/setups/${id}`).then((r) => r.data);
export const fetchHistory = (params = {}) =>
  api.get("/history", { params }).then((r) => r.data);
export const fetchHistoryStats = (params = {}) =>
  api.get("/history/stats", { params }).then((r) => r.data);
export const fetchTimeline = (params = {}) =>
  api.get("/timeline", { params }).then((r) => r.data);

// Auth
export const authSignup = (body) => api.post("/auth/signup", body).then((r) => r.data);
export const authLogin  = (body) => api.post("/auth/login",  body).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe     = () => api.get("/auth/me").then((r) => r.data);

// Billing (user-facing)
export const billingPlans    = () => api.get("/billing/plans").then((r) => r.data);
export const billingMe       = () => api.get("/billing/me").then((r) => r.data);
export const billingWallets  = () => api.get("/billing/wallets").then((r) => r.data);
export const billingSubmit   = (body) => api.post("/billing/submit", body).then((r) => r.data);
export const billingVerify   = (id) => api.post(`/billing/verify/${id}`).then((r) => r.data);
export const billingMyPayments = () => api.get("/billing/payments").then((r) => r.data);

// Admin
export const adminUsers      = (params = {}) => api.get("/admin/users", { params }).then((r) => r.data);
export const adminPatchUser  = (id, body) => api.patch(`/admin/users/${id}`, body).then((r) => r.data);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`).then((r) => r.data);
export const adminUsersExport = async (range = "all") => {
  const res = await api.get(`/admin/users/export`, { params: { range }, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `aureus_users_${range}_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};
export const adminPlans      = () => api.get("/admin/plans").then((r) => r.data);
export const adminPatchPlan  = (key, body) => api.patch(`/admin/plans/${key}`, body).then((r) => r.data);
export const adminConfigGet  = () => api.get("/admin/config").then((r) => r.data);
export const adminConfigPatch= (body) => api.patch("/admin/config", body).then((r) => r.data);
export const adminPayments   = (params = {}) => api.get("/admin/payments", { params }).then((r) => r.data);
export const adminApprovePayment = (id) => api.post(`/admin/payments/${id}/approve`).then((r) => r.data);
export const adminRejectPayment  = (id) => api.post(`/admin/payments/${id}/reject`).then((r) => r.data);
export const adminMarketBudget = () => api.get("/admin/market/budget").then((r) => r.data);
export const adminActivity   = (params = {}) => api.get("/admin/activity", { params }).then((r) => r.data);

// MT5 Auto-Trading (user)
export const mt5Brokers   = () => api.get("/mt5/brokers").then((r) => r.data);
export const mt5Status    = () => api.get("/mt5/status").then((r) => r.data);
export const mt5Connect   = (body) => api.post("/mt5/connect", body).then((r) => r.data);
export const mt5Settings  = (body) => api.patch("/mt5/settings", body).then((r) => r.data);
export const mt5Disconnect= () => api.post("/mt5/disconnect").then((r) => r.data);

// MT5 Auto-Trading (admin)
export const adminMt5Stats    = () => api.get("/admin/mt5/stats").then((r) => r.data);
export const adminMt5Accounts = (params = {}) => api.get("/admin/mt5/accounts", { params }).then((r) => r.data);
export const adminMt5Action   = (userId, action) => api.post(`/admin/mt5/${userId}/${action}`).then((r) => r.data);
export const adminBrokers     = () => api.get("/admin/brokers").then((r) => r.data);
export const adminCreateBroker= (body) => api.post("/admin/brokers", body).then((r) => r.data);
export const adminUpdateBroker= (id, body) => api.put(`/admin/brokers/${id}`, body).then((r) => r.data);
export const adminDeleteBroker= (id) => api.delete(`/admin/brokers/${id}`).then((r) => r.data);

// ML (Phase 4)
export const adminMlStatus    = () => api.get("/admin/ml/status").then((r) => r.data);
export const adminMlTrain     = (body) => api.post("/admin/ml/train", body).then((r) => r.data);
export const adminMlJob       = (id) => api.get(`/admin/ml/jobs/${id}`).then((r) => r.data);
export const adminMlModels    = () => api.get("/admin/ml/models").then((r) => r.data);
export const adminMlActivate  = (id) => api.post(`/admin/ml/models/${id}/activate`).then((r) => r.data);
export const adminMlDelete    = (id) => api.delete(`/admin/ml/models/${id}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
export const fmtPrice = (p) => {
  if (p == null || Number.isNaN(p)) return "—";
  const abs = Math.abs(p);
  const decimals = abs >= 1000 ? 2 : abs >= 100 ? 3 : abs >= 10 ? 4 : 5;
  return Number(p).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const fmtPct = (p) => `${p > 0 ? "+" : ""}${Number(p).toFixed(2)}%`;

export const fmtCountdown = (seconds) => {
  if (seconds == null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const fmtRelative = (isoString) => {
  if (!isoString) return "—";
  const ts = new Date(isoString).getTime();
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(isoString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Map FastAPI error detail (string OR {code,message} OR validation array) → string. */
export const fmtApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof detail === "object" && detail.message) return detail.message;
  return String(detail);
};
